/**
 * Theme Segment - per `Design/Mockups/_Notes - Theme Toggle.md`.
 *
 * Internal building block used by `theme-toggle.ts`. 36 x 36 icon-only segment
 * with 4px corner radius. Selected = `card` bg + `foreground` icon. Unselected
 * = transparent bg + `muted-foreground` icon. Hover on unselected = icon
 * colour bumps to `foreground` only (no background pill).
 *
 * Exported in the public API for game UIs that want to compose their own
 * segmented controls (not just the 3-state theme toggle).
 */

import { iconSvg, type LucideName } from './icons';

export interface ThemeSegmentProps {
  icon: LucideName;
  selected: boolean;
  onSelect: () => void;
  /** Optional accessible label, e.g. "Light theme". */
  ariaLabel?: string;
  /** Reserved for future hover-driven previews. Currently a no-op. */
  hover?: boolean;
}

export interface SegmentHandle {
  update: (props: ThemeSegmentProps) => void;
  destroy: () => void;
}

export function mountThemeSegment(
  target: HTMLElement,
  props: ThemeSegmentProps,
): SegmentHandle {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'gv-theme-segment';

  let current = props;
  let iconEl: SVGElement = iconSvg(current.icon, 20);
  btn.appendChild(iconEl);

  const handler = (): void => current.onSelect();
  btn.addEventListener('click', handler);

  function render(p: ThemeSegmentProps): void {
    if (p.icon !== current.icon) {
      const replacement = iconSvg(p.icon, 20);
      iconEl.replaceWith(replacement);
      iconEl = replacement;
    }
    current = p;
    btn.setAttribute('aria-pressed', p.selected ? 'true' : 'false');
    if (p.ariaLabel !== undefined) {
      btn.setAttribute('aria-label', p.ariaLabel);
    } else {
      btn.removeAttribute('aria-label');
    }
    if (p.hover === true) {
      btn.setAttribute('data-hover', 'true');
    } else {
      btn.removeAttribute('data-hover');
    }
  }

  render(props);
  target.appendChild(btn);

  return {
    update: (next: ThemeSegmentProps): void => {
      render(next);
    },
    destroy: (): void => {
      btn.removeEventListener('click', handler);
      btn.remove();
    },
  };
}
